import { observer } from 'mobx-react-lite';
import { FC, ReactNode } from 'react';
import { FormSpy, FormRenderProps } from 'react-final-form';

import { Form, Field, DateField, TimeField } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup } from '@gem/popups';
import { Button, ButtonTypes, PopupFooter } from '@gem/ui-kit';

import {
  useScheduleSettingsPopupPresenter,
  IScheduleSettingsPopupParams,
  IScheduleSettingsFormValues,
} from './ScheduleSettingsPopup.presenter';
import styles from './ScheduleSettingsPopup.styles.m.css';

export const ScheduleSettingsPopup: FC<ICommonPopupsProps<IScheduleSettingsPopupParams>> = observer(
  (props) => {
    const presenter = useScheduleSettingsPopupPresenter(props);
    const { t } = useTranslation();

    return (
      <Popup
        isOpened
        className={styles.container}
        title={t('schedule_message')}
        theme={'1'}
        onClose={presenter.onClose}
      >
        <Form<IScheduleSettingsFormValues>
          onSubmit={presenter.onSubmit}
          initialValues={presenter.initialValues}
          validate={presenter.onValidateForm}
          subscription={{ submitting: true, invalid: true, validating: true }}
        >
          {({
            handleSubmit,
            submitting,
            validating,
            invalid,
          }: FormRenderProps<IScheduleSettingsFormValues>): ReactNode => (
            <>
              <form
                id="scheduleSettingsForm"
                className={styles.content}
                onSubmit={handleSubmit}
              >
                <Field
                  readOnly
                  className={styles.date}
                  component={DateField}
                  name="date"
                  minDate={presenter.minDate}
                  onFocus={presenter.onFocusDateField}
                />
                <Field
                  ignoreTrimError
                  component={TimeField}
                  name="time"
                />
              </form>

              <PopupFooter className={styles.footer}>
                <Button
                  theme="text-secondary"
                  onClick={presenter.onClose}
                  isDisabled={submitting}
                >
                  {t('common_cancel')}
                </Button>
                <Button
                  theme="primary"
                  type={ButtonTypes.Submit}
                  form="scheduleSettingsForm"
                  isLoading={submitting}
                  isDisabled={invalid || validating}
                >
                  {t('common_schedule')}
                </Button>
              </PopupFooter>

              {/* Fix FinalForm problem with reset errors */}
              <FormSpy<IScheduleSettingsFormValues>
                subscription={{ values: true }}
                render={({ form, values }): ReactNode => {
                  const timeState = form.getFieldState('time');
                  if (timeState?.error) {
                    const errors = presenter.onValidateForm(values);
                    if (Object.keys(errors).length === 0) {
                      form.resetFieldState('time');
                    }
                  }

                  return null;
                }}
              />
            </>
          )}
        </Form>
      </Popup>
    );
  }
);
